import {
  CHEST_SPRITE,
  DARK_KING_KEY_SPRITE,
  HEART_CONTAINER_SPRITE,
  HEART_SPRITE,
  KEY_SPRITE,
  OPEN_CHEST_SPRITE,
  SWORD_SPRITE,
  ITEM_SPRITES
} from '../rendering/Sprites.js'
import { SHOPKEEPER_SPRITES } from '../rendering/CharacterSpriteSheets.js'
import {
  HUD_ARROW_SPRITE,
  HUD_COIN_SPRITE,
  HUD_POTION_SPRITE,
  HUD_SHIELD_SPRITE,
  HUD_SWORD_SPRITE
} from '../rendering/UISprites.js'
import { getWeapon } from './ItemTypes.js'
import { BLOCK_TYPES } from '../world/BlockTypes.js'

const DROP_T = 'transparent'
const pixelDrop = (rows, palette) => ({
  width: 8,
  height: 8,
  pixels: rows.flatMap(row => Array.from(row, pixel => palette[pixel] || DROP_T))
})

const DROP_ARMOR_SPRITE = pixelDrop([
  '..OOOO..', '.OMMMMO.', 'OMLMMHMO', 'OMMMMMMO', '.OMMMMO.', '.OMMMMO.', '.OM..MO.', '..O..O..'
], { O: '#30273a', M: '#8f98aa', L: '#d9e0e8', H: '#656d7e' })
const DROP_MATERIAL_SPRITE = pixelDrop([
  '........', '..OOOO..', '.OLLLHO.', 'OLLLHHHO', 'OHHHHHHO', 'OHHHHHHO', '.OOOOOO.', '........'
], { O: '#3a281d', L: '#c88a4a', H: '#87542f' })
const DROP_FOOD_SPRITE = pixelDrop([
  '........', '..OOOO..', '.ORRRRO.', 'ORRLLRRO', 'ORRRRRRO', '.ORRRRO.', '..OBBO..', '...OO...'
], { O: '#3b241e', R: '#b85a3d', L: '#ef9a63', B: '#e3c18b' })
const DROP_LOOT_SPRITE = pixelDrop([
  '...OO...', '..OLLO..', '.OLLLLO.', 'OLHLHLLO', '.OLLLLO.', '..OLLO..', '...OO...', '........'
], { O: '#312344', L: '#9e62cf', H: '#e2b7ff' })

const enlargePixelSprite = (sprite, width, height) => ({
  width,
  height,
  pixels: Array.from({ length: width * height }, (_, index) => {
    const x = index % width
    const y = Math.floor(index / width)
    const sourceX = Math.min(sprite.width - 1, Math.floor(x * sprite.width / width))
    const sourceY = Math.min(sprite.height - 1, Math.floor(y * sprite.height / height))
    return sprite.pixels[sourceY * sprite.width + sourceX]
  })
})

// Enemy coins use the HUD artwork enlarged by two source pixels. This keeps
// the same palette and silhouette without abandoning the game's pixel grid.
const DROPPED_COIN_SPRITE = enlargePixelSprite(HUD_COIN_SPRITE, 10, 10)

export function getDroppedItemSprite(data) {
  if (!data) return DROP_LOOT_SPRITE
  if (data.type === 'coin') return DROPPED_COIN_SPRITE
  if (data.type === 'potion') return HUD_POTION_SPRITE
  if (data.type === 'shield') return HUD_SHIELD_SPRITE
  if (data.type === 'ammo') return HUD_ARROW_SPRITE
  if (data.type === 'sword') return ITEM_SPRITES[data.id] || HUD_SWORD_SPRITE
  if (data.type === 'armor') return DROP_ARMOR_SPRITE
  if (data.type === 'block') return DROP_MATERIAL_SPRITE
  if (data.type === 'food') return DROP_FOOD_SPRITE
  return ITEM_SPRITES[data.id] || DROP_LOOT_SPRITE
}

export const KEY_CHEST_THEMES = Object.freeze({
  forest: Object.freeze({ body: '#497445', trim: '#8eb26d', dark: '#294b2c' }),
  graveyard: Object.freeze({ body: '#686270', trim: '#aaa1b0', dark: '#403b47' }),
  caves: Object.freeze({ body: '#3d7184', trim: '#79cbd2', dark: '#274856' }),
  emberkeep: Object.freeze({ body: '#a94e32', trim: '#ee9a43', dark: '#642d25' }),
  purpledooms: Object.freeze({ body: '#67408a', trim: '#b879d1', dark: '#39234d' })
})

const keyChestSpriteCache = new Map()

export function getKeyChestSprite(areaId, opened = false) {
  const cacheKey = `${areaId}:${opened ? 'open' : 'closed'}`
  if (keyChestSpriteCache.has(cacheKey)) return keyChestSpriteCache.get(cacheKey)
  const theme = KEY_CHEST_THEMES[areaId] || KEY_CHEST_THEMES.forest
  const source = opened ? OPEN_CHEST_SPRITE : CHEST_SPRITE
  const colors = {
    '#a62': theme.trim,
    '#c84': theme.body,
    '#851': theme.dark,
    '#b73': theme.body,
    '#740': theme.dark,
    '#210': '#17131a'
  }
  const sprite = {
    ...source,
    pixels: source.pixels.map(color => colors[color] || color)
  }
  keyChestSpriteCache.set(cacheKey, sprite)
  return sprite
}

export class ItemManager {
  constructor(game, saveData = null) {
    this.game = game
    this.droppedItems = []
    this.chests = []
    this.spawnedChestZones = new Set()
    this.openedChestIds = new Set()
    
    if (saveData) {
      this.deserialize(saveData)
    }

    this.createKeyChests()
    this.createQuestChests()
    this.createShopChests()
    this.migrateLegacyDarkKingKey()
  }
  
  update(delta) {
    this.checkChestSpawns()
    
    // Update dropped items (bobbing animation & pickup)
    for (let i = this.droppedItems.length - 1; i >= 0; i--) {
      const item = this.droppedItems[i]
      
      // Bobbing
      item.bobTimer = (item.bobTimer || 0) + delta
      item.displayY = item.y + Math.sin(item.bobTimer * 3) * 0.1
      
      // Check pickup
      const dx = this.game.player.x - item.x
      const dy = this.game.player.y - item.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      
      if (dist < 1.2) {
        this.pickupItem(item, i)
      }
    }
    
  }
  
  checkChestSpawns() {
    const zone = this.game.getCurrentZone()
    if (!zone || this.spawnedChestZones.has(zone.name)) return
    
    this.spawnedChestZones.add(zone.name)
    this.spawnChestsForZone(zone)
  }
  
  spawnChestsForZone(zone) {
    for (let i = 0; i < zone.chestCount; i++) {
      const position = zone.getRandomPosition()
      this.createChest(position.x, position.y, zone.name, i)
    }
  }
  
  createChest(x, y, zoneName, index) {
    const id = `chest_${zoneName}_${index}`
    
    // Check if already opened in save data
    const opened = this.openedChestIds.has(id)
    
    const chest = {
      id,
      kind: 'random',
      x,
      y,
      opened,
      loot: this.addGuaranteedChestHearts(
        this.addGuaranteedChestCoins(this.generateLoot(zoneName), index)
      )
    }
    
    this.chests.push(chest)
  }

  addGuaranteedChestCoins(loot, index) {
    const items = Array.isArray(loot) ? loot.map(item => ({ ...item })) : []

    const existingCoins = items
      .filter(item => item.type === 'coin')
      .reduce((sum, item) => sum + (item.count || 1), 0)
    if (existingCoins < 1) {
      items.push({
        type: 'coin',
        id: 'gold_coin',
        name: 'Gold Coin',
        count: 1
      })
    }
    return items
  }

  addGuaranteedChestHearts(loot) {
    const items = Array.isArray(loot) ? loot.map(item => ({ ...item })) : []
    const heartCount = items.filter(item => item.type === 'heart').length
    for (let i = heartCount; i < 2; i++) {
      items.push({
        type: 'heart',
        id: 'heart',
        name: 'Heart',
        count: 1
      })
    }
    return items
  }

  createQuestChests() {
    const branches = this.game.world?.questBranches || []
    for (const branch of branches) {
      if (this.chests.some(chest => chest.id === branch.chest.id)) continue
      this.chests.push({
        id: branch.chest.id,
        kind: 'quest',
        questId: branch.id,
        title: branch.title,
        x: branch.chest.x,
        y: branch.chest.y,
        opened: this.openedChestIds.has(branch.chest.id),
        requiresRoomClear: true,
        reward: { ...branch.reward }
      })
    }
  }

  createKeyChests() {
    for (const keyChest of this.game.world?.keyChests || []) {
      if (this.chests.some(chest => chest.id === keyChest.id)) continue
      this.chests.push({
        ...keyChest,
        kind: 'key',
        title: 'Key Chest',
        requiresRoomClear: true,
        opened: this.openedChestIds.has(keyChest.id)
      })
    }
  }

  createShopChests() {
    for (const shop of this.game.world?.shopSpawns || []) {
      if (this.chests.some(chest => chest.id === shop.id)) continue
      if (shop.postgame && this.chests.some(chest => (
        chest.postgame && chest.shopType === shop.shopType
      ))) continue
      this.chests.push({
        ...shop,
        kind: 'shop',
        title: shop.postgame || shop.fruitOnly ? 'Fruit Market' : 'Upgrade Shop',
        opened: false
      })
    }
  }

  migrateLegacyDarkKingKey() {
    const player = this.game.player
    const chest = this.chests.find(candidate => candidate.isDarkKingKey)
    if (!player || !chest?.opened || player.darkKingKeys > 0 || player.keys <= 0) return false

    const grid = this.game.world?.grids?.find(candidate => candidate.areaId === 'purpledooms')
    if (!grid) return false
    const entrance = grid.getCellCenter(2, 4)
    const doorY = grid.originY + 5 * grid.cellSize
    if (this.game.world.getTileAt(entrance.x, doorY) !== BLOCK_TYPES.LOCKED_DOOR) return false

    player.keys -= 1
    player.addDarkKingKey(1)
    return true
  }
  
  generateLoot(zoneName) {
    const loot = []
    
    const lootTables = {
      forest: [
        { type: 'ammo', id: 'arrow', name: 'Arrows', weight: 35 },
        { type: 'ammo', id: 'bone_arrow', name: 'Bone Arrows', weight: 25 },
        { type: 'shield', id: 'wood_shield', name: 'Wood Shield', defenseHearts: 0.5, weight: 15 },
        { type: 'potion', id: 'health_potion', name: 'Health Potion', healAmount: 1, weight: 25 }
      ],
      graveyard: [
        { type: 'ammo', id: 'bone_arrow', name: 'Bone Arrows', weight: 40 },
        { type: 'ammo', id: 'arrow', name: 'Arrows', weight: 20 },
        { type: 'shield', id: 'bone_shield', name: 'Bone Shield', defenseHearts: 0.5, weight: 15 },
        { type: 'potion', id: 'health_potion', name: 'Health Potion', healAmount: 1, weight: 25 }
      ],
      caves: [
        { type: 'ammo', id: 'arrow', name: 'Arrows', weight: 30 },
        { type: 'ammo', id: 'bone_arrow', name: 'Bone Arrows', weight: 25 },
        { type: 'shield', id: 'iron_shield', name: 'Iron Shield', defenseHearts: 0.5, weight: 20 },
        { type: 'potion', id: 'health_potion', name: 'Health Potion', healAmount: 1, weight: 25 }
      ],
      emberkeep: [
        { type: 'ammo', id: 'fire_arrow', name: 'Fire Arrows', weight: 30 },
        { type: 'ammo', id: 'arrow', name: 'Arrows', weight: 25 },
        { type: 'shield', id: 'ember_shield', name: 'Ember Shield', defenseHearts: 1, weight: 15 },
        { type: 'potion', id: 'health_potion', name: 'Health Potion', healAmount: 1, weight: 30 }
      ],
      purpledooms: [
        { type: 'ammo', id: 'shadow_arrow', name: 'Shadow Arrows', weight: 30 },
        { type: 'ammo', id: 'fire_arrow', name: 'Fire Arrows', weight: 25 },
        { type: 'shield', id: 'royal_shield', name: 'Royal Shield', defenseHearts: 1, weight: 15 },
        { type: 'potion', id: 'health_potion', name: 'Health Potion', healAmount: 1, weight: 30 }
      ],
      swamp: [
        { type: 'potion', id: 'health_potion', name: 'Health Potion', healAmount: 50, weight: 35 },
        { type: 'food', id: 'steak', name: 'Steak', hungerRestore: 50, weight: 25 },
        { type: 'sword', id: 'iron_sword', name: 'Iron Sword', damage: 20, weight: 20 },
        { type: 'armor', id: 'leather_armor', name: 'Leather Armor', defense: 3, weight: 20 }
      ],
      castle: [
        { type: 'sword', id: 'diamond_sword', name: 'Diamond Sword', damage: 30, weight: 15 },
        { type: 'armor', id: 'iron_armor', name: 'Iron Armor', defense: 5, weight: 20 },
        { type: 'block', id: 'diamond_ore', name: 'Diamond', blockType: 11, weight: 25 },
        { type: 'potion', id: 'health_potion', name: 'Health Potion', healAmount: 50, weight: 40 }
      ],
      mistywoods: [
        { type: 'sword', id: 'diamond_sword', name: 'Diamond Sword', damage: 30, weight: 20 },
        { type: 'potion', id: 'health_potion', name: 'Health Potion', healAmount: 75, weight: 30 },
        { type: 'armor', id: 'iron_armor', name: 'Iron Armor', defense: 5, weight: 25 },
        { type: 'food', id: 'golden_apple', name: 'Golden Apple', hungerRestore: 100, weight: 25 }
      ],
      fortress: [
        { type: 'sword', id: 'legendary_sword', name: "Hero's Blade", damage: 40, weight: 10 },
        { type: 'armor', id: 'diamond_armor', name: 'Diamond Armor', defense: 10, weight: 15 },
        { type: 'potion', id: 'health_potion', name: 'Health Potion', healAmount: 100, weight: 40 },
        { type: 'food', id: 'golden_apple', name: 'Golden Apple', hungerRestore: 100, weight: 35 }
      ],
      kingscastle: [
        { type: 'sword', id: 'legendary_sword', name: "Champion's Sword", damage: 45, weight: 30 },
        { type: 'armor', id: 'diamond_armor', name: 'Diamond Armor', defense: 10, weight: 30 },
        { type: 'potion', id: 'health_potion', name: 'Health Potion', healAmount: 100, weight: 40 }
      ]
    }
    
    const table = lootTables[zoneName] || lootTables.forest
    
    const itemCount = 1 + Math.floor(Math.random() * 3)
    
    for (let i = 0; i < itemCount; i++) {
      const totalWeight = table.reduce((sum, item) => sum + item.weight, 0)
      let roll = Math.random() * totalWeight
      
      for (const item of table) {
        roll -= item.weight
        if (roll <= 0) {
          loot.push({
            ...item,
            count: item.type === 'ammo' ? 4 + Math.floor(Math.random() * 6) : 1
          })
          break
        }
      }
    }
    
    return loot
  }

  normalizeLegacyChestLoot(loot) {
    return (Array.isArray(loot) ? loot : []).map(item => {
      if (item.type === 'sword') {
        return { type: 'ammo', id: 'bone_arrow', name: 'Bone Arrows', count: 6 }
      }
      if (item.type === 'armor') {
        return {
          type: 'shield',
          id: 'iron_shield',
          name: 'Iron Shield',
          defenseHearts: 0.5,
          count: 1
        }
      }
      return { ...item }
    })
  }
  
  openChest(chest) {
    if (chest.opened) return false

    if (chest.kind === 'shop') {
      this.game.showUpgradeShop?.(chest)
      return true
    }

    if (this.isQuestChestSealed(chest)) {
      this.game.showItemPopup(
        'A dark seal holds the chest shut. Defeat the enemies in this room.',
        CHEST_SPRITE
      )
      return false
    }
    
    chest.opened = true
    this.openedChestIds.add(chest.id)

    if (chest.kind === 'key') {
      if (chest.isDarkKingKey) this.game.player.addDarkKingKey(1)
      else this.game.player.addKey(1)
      this.game.showItemPopup(
        chest.isDarkKingKey ? "You found the Dark King's Key!" : 'You found a key!',
        chest.isDarkKingKey ? DARK_KING_KEY_SPRITE : KEY_SPRITE
      )
      this.game.quickSave?.()
      return true
    }

    if (chest.kind === 'quest') {
      const applied = this.applyQuestReward(chest.reward, chest.title)
      if (!applied) {
        chest.opened = false
        this.openedChestIds.delete(chest.id)
        return false
      }
      this.game.player.addCoins(1)
      this.game.quickSave?.()
      return true
    }

    // Drop loot
    for (const item of chest.loot) {
      const dropX = chest.x + (Math.random() - 0.5) * 2
      const dropY = chest.y + (Math.random() - 0.5) * 2
      this.spawnItem(item, { x: dropX, y: dropY })
    }

    return true
  }

  getNearestClosedChest(maxRange = this.game.player?.interactRange ?? 2) {
    const player = this.game.player
    if (!player || !Number.isFinite(maxRange) || maxRange <= 0) return null

    let nearest = null
    let nearestDistance = maxRange

    for (const chest of this.chests) {
      if (chest.opened) continue
      if (!this.isChestVisible(chest)) continue
      const distance = Math.hypot(chest.x - player.x, chest.y - player.y)
      if (distance > nearestDistance) continue
      nearest = chest
      nearestDistance = distance
    }

    return nearest
  }

  interactWithNearestChest(
    maxRange = this.game.player?.interactRange ?? 2,
    allowSealed = true
  ) {
    const chest = this.getNearestClosedChest(maxRange)
    if (!chest) return false
    if (!allowSealed && this.isQuestChestSealed(chest)) return false

    // Finding a chest consumes the interaction even when a quest seal rejects
    // the open, so the same press cannot also swing the equipped weapon.
    this.openChest(chest)
    return true
  }

  collidesWithShop(x, y, radius = 0.4) {
    for (const shop of this.chests) {
      if (shop.kind !== 'shop') continue
      if (!this.isShopAvailable(shop)) continue

      const hitsCounter = (
        x + radius > shop.x - 0.98 &&
        x - radius < shop.x + 0.98 &&
        y + radius > shop.y - 0.86 &&
        y - radius < shop.y + 0.73
      )
      if (hitsCounter) return true

      if (this.isShopkeeperPresent(shop)) {
        const shopkeeperX = shop.x
        const shopkeeperY = shop.y - 0.05
        if (Math.hypot(x - shopkeeperX, y - shopkeeperY) < radius + 0.42) {
          return true
        }
      }
    }
    return false
  }

  isShopkeeperPresent(shop) {
    return this.isShopAvailable(shop) && !shop.unattended
  }

  isShopAvailable(shop) {
    if (!shop || shop.kind !== 'shop') return false
    const villageUnlocked = this.game.world?.postgameVillage?.unlocked === true
    return shop.postgame === true
      ? villageUnlocked && this.game.world?.isPlayerInPostgameVillage?.() === true
      : !villageUnlocked
  }

  isChestVisible(chest) {
    if (!chest) return false
    if (chest.kind === 'shop') return this.isShopAvailable(chest)
    return this.game.world?.postgameVillage?.unlocked !== true
  }

  isQuestChestSealed(chest) {
    if (!chest || chest.requiresRoomClear !== true) return false

    if (chest.kind === 'key') {
      const guardIds = (chest.enemySpawns || []).map(spawn => spawn.id).filter(Boolean)
      const deadEnemyIds = this.game.enemyManager?.deadEnemyIds
      // An assigned guard counts as alive until its death ID is recorded. This
      // keeps the ring red even during the frame before the guards spawn.
      if (guardIds.length > 0 && deadEnemyIds instanceof Set) {
        return guardIds.some(id => !deadEnemyIds.has(id))
      }
    }

    return (chest.kind === 'quest' || chest.kind === 'key') &&
      this.game.enemyManager?.hasLivingEnemiesInRoom(chest.x, chest.y) === true
  }

  applyQuestReward(reward, questTitle = '') {
    const prefix = questTitle ? `${questTitle} complete! ` : ''

    if (reward?.kind === 'heart_container') {
      const amount = this.game.player.addHeartContainer(reward.amount || 1)
      this.game.showItemPopup(
        `${prefix}${reward.name || 'Crowned Heart'}! Maximum health increased by ${amount} heart${amount === 1 ? '' : 's'}.`,
        HEART_CONTAINER_SPRITE
      )
      return true
    }

    if (reward?.kind === 'weapon') {
      const weapon = getWeapon(reward.weaponId)
      if (!weapon) return false
      const equipped = this.game.player.equipWeapon(weapon)
      this.game.showItemPopup(
        equipped
          ? `${prefix}You found ${weapon.name}! Attack power increased.`
          : `${prefix}You found ${weapon.name}, but your current weapon is stronger.`,
        SWORD_SPRITE
      )
      return true
    }

    if (reward?.kind === 'supplies') {
      this.game.player.addCoins(5)
      this.game.player.addArrows('arrow', 5)
      this.game.player.addFruit('healthPotion', 1)
      this.game.showItemPopup(
        `${prefix}${reward.name || 'Supply Cache'}! You found 5 coins, 5 arrows, and a health potion.`,
        CHEST_SPRITE
      )
      return true
    }

    return false
  }
  
  spawnItem(itemData, position) {
    const spawnPos = this.findOpenDropPosition(position.x, position.y)
    const droppedItem = {
      data: { ...itemData },
      x: spawnPos.x,
      y: spawnPos.y,
      displayY: spawnPos.y,
      bobTimer: Math.random() * Math.PI * 2
    }
    
    this.droppedItems.push(droppedItem)
  }

  findOpenDropPosition(x, y) {
    if (!this.game.world) return { x, y }

    if (!this.game.world.isSolid(x, y)) {
      return { x, y }
    }

    const maxRadius = 5
    const step = 0.5

    for (let r = step; r <= maxRadius; r += step) {
      for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
        const nx = x + Math.cos(angle) * r
        const ny = y + Math.sin(angle) * r
        if (!this.game.world.isSolid(nx, ny)) {
          return { x: nx, y: ny }
        }
      }
    }

    return { x, y }
  }
  
  pickupItem(item, index) {
    const data = item.data
    this.applyPickup(data)
    this.droppedItems.splice(index, 1)
  }

  applyPickup(data) {
    const player = this.game.player
    if (data.type === 'heart') player.heal(1)
    else if (data.type === 'heart_container') player.addHeartContainer(data.amount || 1)
    else if (data.type === 'key') player.addKey(1)
    else if (data.type === 'sword' && data.damage) player.equipWeapon(data)
    else if (data.type === 'shield') player.equipShield(data)
    else if (data.type === 'ammo') player.addArrows(data.id, data.count || 1)
    else if (data.type === 'coin') player.addCoins(data.count || 1)
    else if (data.type === 'potion' && data.healAmount) {
      player.addFruit('healthPotion', data.count || 1)
    }
  }

  drawKeyChest(renderer, chest) {
    const sealed = this.isQuestChestSealed(chest)
    if (!chest.opened) {
      if (sealed) {
        renderer.fillCircle(chest.x, chest.y, 0.78, 'rgba(224, 38, 48, 0.48)')
        renderer.fillCircle(chest.x, chest.y, 0.63, 'rgba(116, 12, 22, 0.42)')
      } else {
        renderer.fillCircle(chest.x, chest.y, 0.78, 'rgba(255, 220, 72, 0.5)')
        renderer.fillCircle(chest.x, chest.y, 0.63, 'rgba(143, 104, 12, 0.38)')
      }
    }
    renderer.drawSprite(
      getKeyChestSprite(chest.areaId, chest.opened),
      chest.x - 0.5,
      chest.y - 0.5,
      0,
      1
    )
  }
  
  render(renderer) {
    // Render chests
    for (const chest of this.chests) {
      if (!this.isChestVisible(chest)) continue
      if (!this.game.world.isPositionInActiveLevel(chest.x, chest.y)) continue
      if (!this.game.world.isTileRevealed(chest.x, chest.y)) continue
      if (chest.kind === 'shop') {
        const themes = {
          forest: ['#d94c2f', '#f1c75b', '#6b351d'],
          graveyard: ['#674d82', '#b9a0cf', '#493650'],
          caves: ['#287f95', '#82d7d3', '#31556c'],
          emberkeep: ['#c7462d', '#f0a43a', '#642d25'],
          purpledooms: ['#63349b', '#b77be0', '#342044']
        }
        const [primary, accent, wood] = themes[chest.areaId] || themes.forest
        const darkWood = '#3d261d'
        const counter = '#98562f'

        // Floor mat, back wall, roof cap, and five-striped awning.
        renderer.fillRect(chest.x - 0.92, chest.y + 0.61, 1.84, 0.12, darkWood)
        renderer.fillRect(chest.x - 0.7, chest.y - 0.48, 1.4, 0.92, '#251d22')
        renderer.fillRect(chest.x - 0.98, chest.y - 0.86, 1.96, 0.14, darkWood)
        for (let stripe = 0; stripe < 5; stripe++) {
          renderer.fillRect(
            chest.x - 0.9 + stripe * 0.36,
            chest.y - 0.72,
            0.36,
            0.3,
            stripe % 2 === 0 ? primary : accent
          )
        }
        renderer.fillRect(chest.x - 0.96, chest.y - 0.44, 1.92, 0.08, darkWood)

        // Highlighted support posts and a stocked rear shelf.
        renderer.fillRect(chest.x - 0.76, chest.y - 0.42, 0.15, 1.05, wood)
        renderer.fillRect(chest.x - 0.72, chest.y - 0.4, 0.04, 1.0, accent)
        renderer.fillRect(chest.x + 0.61, chest.y - 0.42, 0.15, 1.05, wood)
        renderer.fillRect(chest.x + 0.65, chest.y - 0.4, 0.04, 1.0, accent)
        renderer.fillRect(chest.x - 0.55, chest.y + 0.03, 1.1, 0.08, wood)
        renderer.fillRect(chest.x - 0.5, chest.y - 0.12, 0.14, 0.15, primary)
        renderer.fillRect(chest.x + 0.36, chest.y - 0.12, 0.14, 0.15, accent)

        if (this.isShopkeeperPresent(chest)) {
          const shopkeeper = SHOPKEEPER_SPRITES[chest.areaId] || SHOPKEEPER_SPRITES.forest
          renderer.drawSprite(shopkeeper, chest.x - 0.5, chest.y - 0.55, 0, 1)
        }

        // Paneled counter, corner bolts, coin sign, and hanging lantern.
        renderer.fillRect(chest.x - 0.84, chest.y + 0.25, 1.68, 0.4, darkWood)
        renderer.fillRect(chest.x - 0.78, chest.y + 0.31, 1.56, 0.28, counter)
        renderer.fillRect(chest.x - 0.7, chest.y + 0.36, 0.56, 0.18, wood)
        renderer.fillRect(chest.x + 0.14, chest.y + 0.36, 0.56, 0.18, wood)
        renderer.fillRect(chest.x - 0.84, chest.y + 0.23, 1.68, 0.08, accent)
        renderer.fillCircle(chest.x - 0.72, chest.y + 0.43, 0.04, accent)
        renderer.fillCircle(chest.x + 0.72, chest.y + 0.43, 0.04, accent)
        renderer.fillCircle(chest.x + 0.48, chest.y - 0.15, 0.2, '#ffd65a')
        renderer.fillCircle(chest.x + 0.48, chest.y - 0.15, 0.1, '#a96713')
        renderer.fillRect(chest.x - 0.58, chest.y - 0.6, 0.08, 0.18, darkWood)
        renderer.fillCircle(chest.x - 0.54, chest.y - 0.35, 0.13, '#ffd65a')
        renderer.fillCircle(chest.x - 0.54, chest.y - 0.35, 0.06, '#fff0a4')
        continue
      }

      if (chest.kind === 'quest') {
        if (!chest.opened) {
          const glow = this.isQuestChestSealed(chest)
            ? 'rgba(139, 80, 190, 0.35)'
            : chest.kind === 'shop'
              ? 'rgba(80, 210, 255, 0.4)'
              : 'rgba(255, 214, 92, 0.35)'
          renderer.fillCircle(chest.x, chest.y, 0.75, glow)
          renderer.drawSprite(CHEST_SPRITE, chest.x - 0.5, chest.y - 0.5)
        } else {
          renderer.drawSprite(OPEN_CHEST_SPRITE, chest.x - 0.5, chest.y - 0.5)
        }
        continue
      }

      if (chest.kind === 'key') {
        this.drawKeyChest(renderer, chest)
        continue
      }

      if (!chest.opened) {
        renderer.drawSprite(CHEST_SPRITE, chest.x - 0.5, chest.y - 0.5)
      } else {
        renderer.drawSprite(OPEN_CHEST_SPRITE, chest.x - 0.5, chest.y - 0.5)
      }
    }
    
    // Render dropped items
    for (const item of this.droppedItems) {
      if (!this.game.world.isPositionInActiveLevel(item.x, item.y)) continue
      if (!this.game.world.isTileRevealed(item.x, item.y)) continue
      if (item.data.type === 'heart') {
        renderer.drawSprite(HEART_SPRITE, item.x - 0.5, item.displayY - 0.5, 0, 1)
      } else if (item.data.type === 'heart_container') {
        renderer.drawSprite(HEART_CONTAINER_SPRITE, item.x - 0.5, item.displayY - 0.5, 0, 1)
      } else {
        renderer.drawSprite(
          getDroppedItemSprite(item.data),
          item.x - 0.5,
          item.displayY - 0.5,
          0,
          1
        )
      }
    }
  }
  
  serialize() {
    return {
      spawnedChestZones: Array.from(this.spawnedChestZones),
      openedChestIds: Array.from(this.openedChestIds),
      chests: this.chests
        // Shops and quest chests are authored world objects. Saving copies of
        // them lets legacy layouts accumulate beside the current definitions.
        .filter(chest => chest.kind !== 'quest' && chest.kind !== 'shop')
        .map(chest => ({
          ...chest,
          loot: Array.isArray(chest.loot) ? chest.loot.map(item => ({ ...item })) : []
        })),
      droppedItems: this.droppedItems.map(i => ({
        data: i.data,
        x: i.x,
        y: i.y
      }))
    }
  }
  
  deserialize(data) {
    if (data) {
      this.spawnedChestZones = new Set(
        (Array.isArray(data.spawnedChestZones) ? data.spawnedChestZones : [])
          .filter(zone => typeof zone === 'string')
      )
      this.openedChestIds = new Set(
        (Array.isArray(data.openedChestIds) ? data.openedChestIds : [])
          .filter(id => typeof id === 'string')
      )
      if (Array.isArray(data.chests)) {
        this.chests = data.chests
          .filter(chest => (
            chest &&
            typeof chest === 'object' &&
            typeof chest.id === 'string' &&
            Number.isFinite(chest.x) &&
            Number.isFinite(chest.y) &&
            chest.kind !== 'quest' &&
            chest.kind !== 'shop'
          ))
          .map(chest => ({
            ...chest,
            kind: chest.kind || 'random',
            loot: chest.kind === 'random'
              ? this.addGuaranteedChestHearts(
                  this.addGuaranteedChestCoins(
                    this.normalizeLegacyChestLoot(chest.loot),
                    Number.parseInt(chest.id?.split('_').at(-1), 10)
                  )
                )
              : Array.isArray(chest.loot)
                ? chest.loot.map(item => ({ ...item }))
                : []
          }))
        for (const chest of this.chests) {
          if (chest.opened) this.openedChestIds.add(chest.id)
        }
      } else if (this.spawnedChestZones.size > 0) {
        // Legacy saves tracked spawned zones without preserving their chest instances.
        this.spawnedChestZones.clear()
      }
      if (Array.isArray(data.droppedItems)) {
        this.droppedItems = data.droppedItems
          .filter(item => (
            item &&
            typeof item.data === 'object' &&
            Number.isFinite(item.x) &&
            Number.isFinite(item.y)
          ))
          .map(item => ({
            ...item,
            displayY: item.y,
            bobTimer: 0
          }))
      }
    }
    // Rebuild every authored chest from the current world definition. This
    // also migrates old saves that contained duplicate village shops.
    this.createKeyChests()
    this.createQuestChests()
    this.createShopChests()
  }
}
